import { router as getRouter } from './routes';
import { RouterProvider } from './compat/router-shim';
import { tracker } from './services/tracker';
import { useEffect, useRef } from 'react';
import './store/network/initialize';
import './store/update/initialize';
import { useHydrateAtoms } from 'jotai/utils';
import { queryClientAtom } from 'jotai-tanstack-query';
import { queryClient } from './clients/queryClient';
import { useMsal } from '@azure/msal-react';
import { getTooltipInitializer } from './data/database.tooltips.initializer';
import { inspectionDB } from './data/database.structure';
import { prefetchHelpImages } from './services/helpImagePrefetchService';
import { useSetAtom } from 'jotai';
import { startWorkboxPollingAtom } from './store/network/indexhtmlState';
import { initStorageResilience } from './services/storageResilienceService';
import { initSyncDrainTriggers } from './services/inspectionSync/initSyncDrain';
import { useSessionLifecycle } from './hooks/useSessionLifecycle';
import { session } from './services/auth/session';
import { toast } from './store/toast';
import {
  EToastType,
  TOAST_DURATIONS,
} from './components/molecules/ToastMessage';

// tracker moved to services/tracker.ts so leaf components can import it without
// dragging App's whole route/PWA import graph with it. Re-exported for the call
// sites that historically import it from '../App'.
export { tracker };

function App() {
  const startPolling = useSetAtom(startWorkboxPollingAtom);
  const isTriggeredRef = useRef<boolean>(false);
  const isTooltipInitTriggeredRef = useRef<boolean>(false);
  const { accounts } = useMsal();
  useSessionLifecycle();

  useEffect(() => {
    if (!isTriggeredRef.current) {
      isTriggeredRef.current = true;

      const init = async () => {
        await deleteOldFlutterData();
        deleteOldData();
        initializeTracker();
        // Request durable storage + warn if the device is at eviction risk (offline data safety).
        void initStorageResilience((message) =>
          toast.add({
            message,
            type: EToastType.warning,
            isOpen: true,
            options: { duration: TOAST_DURATIONS.ERROR },
          }),
        ).catch((error) =>
          console.error('Storage resilience init failed:', error),
        );
      };

      const deleteOldData = () => {
        localStorage.removeItem('app_version_hash');
      };

      const deleteOldFlutterData = async () => {
        // One-shot: the legacy Flutter/moor databases can only reappear if an old app version
        // runs again, and this cleanup otherwise costs IndexedDB work on every boot.
        if (localStorage.getItem('legacy_flutter_cleanup_done') === 'true') {
          return;
        }

        let currentLoadingTime = performance.now();

        const fallbackDbNames = ['moor_databases', 'observationimages', 'logs'];

        const deleteDb = (name: string) => {
          indexedDB.deleteDatabase(name);
          console.log(`Deleted IndexedDB database: ${name}`);
        };

        // Delete IndexedDB databases
        fallbackDbNames.forEach(deleteDb);

        // Delete localStorage keys starting with "flutter."
        const keysToDelete: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('flutter.')) {
            keysToDelete.push(key);
          }
        }

        keysToDelete.forEach((key) => {
          localStorage.removeItem(key);
          console.log(`Deleted localStorage key: ${key}`);
        });

        localStorage.setItem('legacy_flutter_cleanup_done', 'true');

        console.log(
          'Delete Flutter data running time ',
          (performance.now() - currentLoadingTime).toFixed(2) + 'ms',
        );
      };

      const initializeTracker = () => {
        try {
          // Initialize tracker with user if available
          if (accounts.length > 0) {
            tracker.setUserID(accounts[0].username || '');
          } else {
            const email = session.claims()?.email;
            if (email) {
              tracker.setUserID(email);
            }
          }
        } catch (error) {
          console.error('Error initializing tracker:', error);
        }
      };

      init();
      tracker.start();
    }
  }, [accounts]);

  // Tooltip/help content is loaded after sign-in (alongside the post-login
  // sync) so the parse/cache work never competes with the login screen.
  // Fire-and-forget — tooltip reads lazily initialize themselves if this
  // hasn't finished yet.
  useEffect(() => {
    if (isTooltipInitTriggeredRef.current) {
      return;
    }

    const isAuthenticated = accounts.length > 0 || !!session.getToken();
    if (!isAuthenticated) {
      return;
    }

    isTooltipInitTriggeredRef.current = true;
    getTooltipInitializer(inspectionDB)
      .initializeTooltips()
      .catch((error) => console.error('Error initializing tooltips:', error));
    prefetchHelpImages().catch((error) =>
      console.error('Error prefetching help images:', error),
    );
    // Durable delivery (P0): drain submitted-but-undelivered inspections on startup,
    // reconnect and app-foreground. Idempotent; every trigger is guarded inside drain().
    initSyncDrainTriggers();
  }, [accounts]);

  useEffect(() => {
    // Start monitoring once at startup
    const cleanup = startPolling();

    return () => {
      if (cleanup) cleanup();
    };
  }, [startPolling]);

  return <RouterProvider router={getRouter()} />;
}
export const HydrateAtoms = ({ children }: { children: React.ReactNode }) => {
  useHydrateAtoms(new Map([[queryClientAtom, queryClient]]));
  return children;
};
export default App;
